import {
  getLeoUuid,
  LeoUUID,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class GetPresignedDownloadURLForSurveyRulesRPC implements
  LeoRPC<GetPresignedDownloadURLForSurveyRulesRPC.Request, GetPresignedDownloadURLForSurveyRulesRPC.Response, GetPresignedDownloadURLForSurveyRulesRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedDownloadURLForSurveyRulesRPC.Request
  ): Promise<LeoRPCResult<GetPresignedDownloadURLForSurveyRulesRPC.Response, GetPresignedDownloadURLForSurveyRulesRPC.Errors.Errors>>;
}

export namespace GetPresignedDownloadURLForSurveyRulesRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedDownloadURLForSurveyRulesRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID
    ) {
      this.surveyId = surveyId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      return new Request(
        surveyId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLForSurveyRulesRPC(surveyId='${this.surveyId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedDownloadURLForSurveyRulesResponse: boolean = true;
  
    readonly documentURL: URL;
  
  
    constructor(
    documentURL: URL
    ) {
      this.documentURL = documentURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      return new Response(
        documentURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLForSurveyRules(documentURL='${this.documentURL}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    RulesRecordNotFound = "RULES_RECORD_NOT_FOUND"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetPresignedDownloadURLForSurveyRulesRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface RulesRecordNotFoundDTO {
      code: string;
    }
    
    export class RulesRecordNotFound extends LeoRPCError<RPCError> {
      readonly isRulesRecordNotFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.RulesRecordNotFound;
        super(code);
      }
    
    
      toDTO(): RulesRecordNotFoundDTO {
        const dto: RulesRecordNotFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    RulesRecordNotFound.prototype.toString = function toString(): string {
      return `GetPresignedDownloadURLForSurveyRulesRPC.RULES_RECORD_NOT_FOUND()`;
    };
    

    export type Errors = InvalidSurveyId | RulesRecordNotFound;
    export type ErrorsDTO = InvalidSurveyIdDTO | RulesRecordNotFoundDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.RulesRecordNotFound:
          return new RulesRecordNotFound();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends GetPresignedDownloadURLForSurveyRulesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedDownloadURLForSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetPresignedDownloadURLForSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseRulesRecordNotFoundError extends GetPresignedDownloadURLForSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRulesRecordNotFoundError";
  }
}

